/**
 * 原生弹窗兜底拦截。
 *
 * 需求明确要求「弹窗不可以调用浏览器系统弹窗」。开发模式下直接把
 * `window.alert / confirm / prompt` 换成会报错的替身，这样任何遗漏的调用
 * 都不会弹出系统框，而是在控制台留下明确线索。
 *
 * 注意：原生 API 是同步的，无法用异步浮层等价替换，因此这里返回安全默认值
 * （`confirm → false`、`prompt → null`）并响亮地失败，而不是静默假装成功。
 */

const INSTALL_FLAG = '__havorixNativeDialogGuard__';

type GuardedWindow = Window & { [INSTALL_FLAG]?: boolean };

export interface NativeDialogGuardOptions {
  /** 即使不是开发模式也安装拦截（例如预发环境） */
  force?: boolean;
}

export function installNativeDialogGuard(options: NativeDialogGuardOptions = {}): void {
  const target = window as GuardedWindow;
  if (target[INSTALL_FLAG]) return;

  const isDev = Boolean(import.meta.env?.DEV);
  if (!isDev && !options.force) return;

  const report = (api: string, args: unknown[]) => {
    console.error(
      `[havorix] 检测到原生弹窗调用 window.${api}(${args.map((a) => JSON.stringify(a)).join(', ')})。` +
        '本平台禁止使用浏览器系统弹窗，请改用统一浮层系统：' +
        "import { overlay } from '@/overlay';\n" +
        "await overlay.confirm({ placementKey, trigger: event.currentTarget, title, description });",
    );
  };

  window.alert = (...args: unknown[]) => {
    report('alert', args);
  };

  window.confirm = (...args: unknown[]) => {
    report('confirm', args);
    return false;
  };

  window.prompt = (...args: unknown[]) => {
    report('prompt', args);
    return null;
  };

  target[INSTALL_FLAG] = true;
  console.info('[havorix] 已启用原生弹窗拦截（仅开发模式）。');
}
